import { Ics2Referral, Ics2ReferralDraft } from 'store/declarations/eu/ics2/customs-requests/types';

export const FormFields = {
    SCREENING_DETAILS: {
        RESULT: 'screeningDetails.result',
        METHODS: 'screeningDetails.methods',
        CODE: 'screeningDetails.code',
        NOTES: 'screeningDetails.notes',
    },
    AUTHORIZED_PERSON: {
        TYPE: 'authorizedPerson.typeOfPerson',
        NAME: 'authorizedPerson.name',
        ID: 'authorizedPerson.identificationNumber',
    },
    FACILITY: {
        STREET: 'facilityAddress.street',
        NUMBER: 'facilityAddress.number',
        PO_BOX: 'facilityAddress.poBox',
        CITY: 'facilityAddress.city',
        POST_CODE: 'facilityAddress.postCode',
        COUNTRY: 'facilityAddress.country',
    },
} as const;

/** IE3R03 requires between two and nine screening methods on every result. */
export const MIN_SCREENING_METHODS = 2;
export const MAX_SCREENING_METHODS = 9;

export type ScreeningDetailsValues = {
    result: string | undefined;
    methods: string[];
    code: string | undefined;
    notes: string | undefined;
};

export type AuthorizedPersonValues = {
    typeOfPerson: string | undefined;
    name: string | undefined;
    identificationNumber: string | undefined;
};

export type FacilityAddressValues = {
    street: string | undefined;
    number: string | undefined;
    poBox: string | undefined;
    city: string | undefined;
    postCode: string | undefined;
    country: string | undefined;
};

export type FormValues = {
    screeningDetails: ScreeningDetailsValues;
    authorizedPerson: AuthorizedPersonValues;
    facilityAddress: FacilityAddressValues;
};

const hasAnyValue = (values: Record<string, string | undefined>) =>
    Object.values(values).some((value) => Boolean(value?.trim()));

export const toReferralDraft = (values: FormValues): Ics2ReferralDraft => {
    const { screeningDetails, authorizedPerson, facilityAddress } = values;

    return {
        result: screeningDetails.result || undefined,
        screeningMethods: screeningDetails.methods?.length ? screeningDetails.methods : undefined,
        additionalInformation:
            screeningDetails.code || screeningDetails.notes?.trim()
                ? [{ code: screeningDetails.code || undefined, text: screeningDetails.notes?.trim() || undefined }]
                : undefined,
        authorizedPerson: hasAnyValue(authorizedPerson) ? authorizedPerson : undefined,
        facilityAddress: hasAnyValue(facilityAddress) ? facilityAddress : undefined,
    };
};

const recommendedMethods = (referral: Ics2Referral): string[] =>
    (referral.requestDetails?.recommendedHRCMScreeningMethod
        ?.map((entry) => entry.method)
        .filter(Boolean) as string[]) ?? [];

/**
 * The inverse of `toReferralDraft`, kept beside it so the two cannot drift.
 *
 * Strictly what was saved - nothing is filled in on the way out. It is what re-validates a saved
 * draft with no form mounted, so anything added here would be read back as an answer the user
 * never gave. Seeding belongs in `initialFormValues`.
 */
export const fromReferralDraft = (referral: Ics2Referral): FormValues => {
    const saved = referral.responseDraft;
    const information = saved?.additionalInformation?.[0];
    return {
        screeningDetails: {
            result: saved?.result,
            methods: saved?.screeningMethods ?? [],
            code: information?.code,
            notes: information?.text,
        },
        authorizedPerson: {
            typeOfPerson: saved?.authorizedPerson?.typeOfPerson,
            name: saved?.authorizedPerson?.name,
            identificationNumber: saved?.authorizedPerson?.identificationNumber,
        },
        facilityAddress: {
            street: saved?.facilityAddress?.street,
            number: saved?.facilityAddress?.number,
            poBox: saved?.facilityAddress?.poBox,
            city: saved?.facilityAddress?.city,
            postCode: saved?.facilityAddress?.postCode,
            country: saved?.facilityAddress?.country,
        },
    };
};

export const initialFormValues = (referral: Ics2Referral): FormValues => {
    const values = fromReferralDraft(referral);
    if (values.screeningDetails.methods.length) return values;

    return {
        ...values,
        screeningDetails: { ...values.screeningDetails, methods: recommendedMethods(referral) },
    };
};
